import type { Card, CardMessage, MessagePart, PendingReview, PromptContext } from '../types';
import { DEFAULT_TITLE } from '../types';
import { normalizeLanguage } from '../languages';
import { newId } from '../util/id';

type Loose = Record<string, unknown>;

const asString = (value: unknown, fallback = ''): string => (typeof value === 'string' ? value : fallback);
const asNumber = (value: unknown, fallback: number): number => (typeof value === 'number' && Number.isFinite(value) ? value : fallback);

function normalizePart(raw: unknown): MessagePart | null {
  if (!raw || typeof raw !== 'object') return null;
  const part = raw as Loose;
  switch (part.type) {
    case 'text':
    case 'reasoning':
    case 'error':
      return { type: part.type, text: asString(part.text) };
    case 'proposal': {
      const p = (part.proposal ?? {}) as Loose;
      const status = p.status;
      return {
        type: 'proposal',
        proposal: {
          id: asString(p.id, newId()),
          summary: asString(p.summary),
          status: status === 'accepted' || status === 'rejected' || status === 'superseded' ? status : 'pending',
          ...(typeof p.code === 'string' ? { code: p.code, language: normalizeLanguage(asString(p.language)) } : {}),
          ...(typeof p.spec === 'string' ? { spec: p.spec } : {}),
        },
      };
    }
    default:
      return null;
  }
}

function normalizeMessage(raw: unknown, fallbackTime: number): CardMessage | null {
  if (!raw || typeof raw !== 'object') return null;
  const m = raw as Loose;
  if (m.role !== 'user' && m.role !== 'assistant') return null;
  const parts = Array.isArray(m.parts) ? m.parts.map(normalizePart).filter((p): p is MessagePart => p !== null) : [];
  const message: CardMessage = { id: asString(m.id, newId()), role: m.role, parts, createdAt: asNumber(m.createdAt, fallbackTime) };
  // Before cards, only the code was attached, under `codeContext`.
  const context = (m.context ?? (m.codeContext ? { code: m.codeContext } : undefined)) as PromptContext | undefined;
  if (context && typeof context === 'object') message.context = context;
  if (typeof m.action === 'string') message.action = m.action;
  if (m.sent && typeof m.sent === 'object') message.sent = m.sent as CardMessage['sent'];
  if (m.native && typeof m.native === 'object') message.native = m.native as CardMessage['native'];
  if (m.usage && typeof m.usage === 'object') message.usage = m.usage as CardMessage['usage'];
  return message;
}

/**
 * Bring a stored card, or a chat saved before cards existed, to the current shape. Unknown fields are
 * dropped (the old Code/Tutor `mode` among them), and anything missing gets a safe default.
 */
export function normalizeCard(raw: unknown): Card {
  const c = (raw && typeof raw === 'object' ? raw : {}) as Loose;
  const now = Date.now();
  const createdAt = asNumber(c.createdAt, now);
  const review = c.review as Partial<PendingReview> | null | undefined;
  const spec = asString(c.spec);
  return {
    id: asString(c.id, newId()),
    title: asString(c.title).trim() || DEFAULT_TITLE,
    createdAt,
    updatedAt: asNumber(c.updatedAt, createdAt),
    messages: Array.isArray(c.messages)
      ? c.messages.map((m) => normalizeMessage(m, createdAt)).filter((m): m is CardMessage => m !== null)
      : [],
    code: asString(c.code),
    language: normalizeLanguage(asString(c.language, 'python')),
    spec,
    specSync: c.specSync && typeof c.specSync === 'object' ? (c.specSync as Card['specSync']) : null,
    ...(Array.isArray(c.suggestions) ? { suggestions: c.suggestions.filter((s): s is string => typeof s === 'string') } : {}),
    review:
      review && typeof review.original === 'string'
        ? { proposalId: review.proposalId ?? null, original: review.original, originalSpec: review.originalSpec ?? spec }
        : null,
  };
}
